import React from "react";
import { COURTS, MATTER_TYPES } from "@/lib/aidPlannerSchema";
import { COURT_LOCATIONS, withCurrentString } from "@/lib/formChoices";
import { getPlannerAppearanceOptions } from "@/lib/aidPlannerCriminalFees";
import { BooleanField, SelectField } from "./PlannerFields";

export default function MatterDetailsTile({
  planner,
  setClientField,
  setMatterField,
}) {
  const appearanceOptions =
    planner.matter.matterType === "Criminal"
      ? getPlannerAppearanceOptions(
          planner.matter.court,
          planner,
          planner.matter.appearanceType
        )
      : [];

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden border-t-4 border-t-purple-600 flex flex-col h-full">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-800">Matter Details</h2>
        <div className="text-slate-300">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </div>
      </div>
      
      <div className="p-4 flex-1 space-y-4">
        {/* Client Info Block */}
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Client Profile</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">First Name</span>
              <input
                value={planner.client.firstName || ""}
                onChange={(e) => setClientField("firstName", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>
            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">Last Name</span>
              <input
                value={planner.client.lastName || ""}
                onChange={(e) => setClientField("lastName", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>
            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">File Number</span>
              <input
                value={planner.client.fileNumber || ""}
                onChange={(e) => setClientField("fileNumber", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>
            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">Date of Birth</span>
              <input
                type="date"
                value={planner.client.dateOfBirth || ""}
                onChange={(e) => setClientField("dateOfBirth", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>
          </div>
        </div>

        {/* Case Info Block */}
        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Case Specifications</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <SelectField
              required
              compact
              label="Matter type"
              value={planner.matter.matterType}
              onChange={(value) => setMatterField("matterType", value)}
              options={MATTER_TYPES}
            />
            <SelectField
              required
              compact
              label="Court"
              value={planner.matter.court}
              onChange={(value) => setMatterField("court", value)}
              options={COURTS}
            />
            <SelectField
              required
              compact
              label="Court Location"
              value={planner.matter.location}
              onChange={(value) => setMatterField("location", value)}
              options={withCurrentString(COURT_LOCATIONS, planner.matter.location)}
            />
            
            {planner.matter.matterType === "Criminal" ? (
              <SelectField
                required
                compact
                label="Next listing type"
                value={planner.matter.appearanceType}
                onChange={(value) => setMatterField("appearanceType", value)}
                options={appearanceOptions}
              />
            ) : (
              <label className="space-y-0.5 text-[11px]">
                <span className="font-semibold text-slate-500">Next listing type *</span>
                <input
                  value={planner.matter.appearanceType || ""}
                  onChange={(e) => setMatterField("appearanceType", e.target.value)}
                  className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
                />
              </label>
            )}

            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">Next listing date *</span>
              <input
                type="date"
                value={planner.matter.nextAppearanceDate || ""}
                onChange={(e) => setMatterField("nextAppearanceDate", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>

            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">Lawyer</span>
              <input
                value={planner.matter.lawyer || ""}
                onChange={(e) => setMatterField("lawyer", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
              />
            </label>

            <BooleanField
              compact
              label="Client in custody?"
              value={planner.matter.isCustody}
              onChange={(value) => setMatterField("isCustody", value)}
            />

            <label className="space-y-0.5 text-[11px]">
              <span className="font-semibold text-slate-500">Head Charge</span>
              <input
                value={planner.matter.headCharge || ""}
                onChange={(e) => setMatterField("headCharge", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-lg px-2.5 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all text-xs"
                placeholder="e.g. Trafficking commercial quantity"
              />
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}
